import { cloneBlockTree } from "./block.js";

export function normalizeTheme(theme) {
  if (!theme) return "dark";
  const raw = String(theme);
  if (raw === "Light") return "light";
  if (raw === "Dark") return "dark";
  const lower = raw.toLowerCase();
  return lower === "light" ? "light" : "dark";
}

export function getChrome(doc) {
  if (doc?.chrome && typeof doc.chrome === "object") return doc.chrome;
  return { frame: [], pages: {} };
}

export function getPages(doc) {
  const pages = getChrome(doc).pages;
  return pages && typeof pages === "object" ? pages : {};
}

export function getWidgets(doc) {
  return doc?.widgets && typeof doc.widgets === "object" ? doc.widgets : {};
}

export function listPageKeys(doc) {
  return Object.keys(getPages(doc));
}

export function listWidgetCatalogNames(doc) {
  return Object.keys(getWidgets(doc));
}

export function listChromeFrame(doc) {
  const frame = getChrome(doc).frame;
  return Array.isArray(frame) ? frame : [];
}

export function listChromeFrameRefs(doc) {
  return listChromeFrame(doc)
    .map((entry) => (typeof entry?.ref === "string" ? entry.ref.trim() : ""))
    .filter(Boolean);
}

const FRAME_SLOT_ORDER = ["header", "sidebar", "footer", "auth", "notice", "wallet", "casino_launch"];

/** Widgets placed in the frame. Slot `data` stays on the slot; root `dataset` is not read. */
export function listFrameWidgets(doc) {
  const fromFrame = listChromeFrameRefs(doc);
  if (fromFrame.length) {
    return [...new Set(fromFrame)].map((ref) => ({ ref }));
  }
  const chrome = getChrome(doc);
  const slots = Object.keys(chrome).filter((key) => {
    if (key === "pages" || key === "frame") return false;
    const slot = chrome[key];
    return Boolean(slot && typeof slot === "object" && typeof slot.widget === "string" && slot.widget.trim());
  });
  slots.sort((left, right) => {
    const leftIndex = FRAME_SLOT_ORDER.indexOf(left);
    const rightIndex = FRAME_SLOT_ORDER.indexOf(right);
    return (leftIndex < 0 ? FRAME_SLOT_ORDER.length : leftIndex)
      - (rightIndex < 0 ? FRAME_SLOT_ORDER.length : rightIndex);
  });
  const seen = new Set();
  const entries = [];
  for (const slot of slots) {
    const ref = chrome[slot].widget.trim();
    if (seen.has(ref)) continue;
    seen.add(ref);
    entries.push({ ref, slot });
  }
  return entries;
}

export function frameEntryForRef(doc, ref) {
  return listChromeFrame(doc).find((entry) => entry?.ref === ref) ?? null;
}

/** Stored frame item, or the chrome slot that places this widget. */
export function framePlacement(doc, ref) {
  const frame = listChromeFrame(doc);
  const index = frame.findIndex((entry) => entry?.ref === ref);
  if (index >= 0) return { node: frame[index], path: ["frame", String(index)] };
  const entry = listFrameWidgets(doc).find((item) => item.ref === ref);
  const slot = entry?.slot;
  const node = slot ? getChrome(doc)[slot] : null;
  if (!slot || !node || typeof node !== "object") return null;
  return { node, path: [slot] };
}

/** `chrome.frame` when it is stored, otherwise the slots the frame tree lists. */
export function frameRootContract(doc) {
  const chrome = getChrome(doc);
  if (Array.isArray(chrome.frame)) return chrome.frame;
  const placed = {};
  for (const entry of listFrameWidgets(doc)) {
    if (!entry.slot) continue;
    const slot = chrome[entry.slot];
    if (slot && typeof slot === "object") placed[entry.slot] = slot;
  }
  return Object.keys(placed).length ? placed : null;
}

export function paletteBlocks(doc) {
  const elements = doc?.elements;
  if (Array.isArray(elements)) return elements;
  if (elements && typeof elements === "object") return Object.values(elements);
  return [];
}

export function paletteEntryName(entry, index) {
  if (entry && typeof entry.id === "string" && entry.id.trim()) return entry.id.trim();
  return String(index);
}

export function paletteIndexByName(doc, name) {
  const blocks = paletteBlocks(doc);
  for (let i = 0; i < blocks.length; i += 1) {
    if (paletteEntryName(blocks[i], i) === name) return i;
  }
  return -1;
}

/** Map palette name → block (editor tree). */
export function getGlobalElements(doc) {
  const out = {};
  paletteBlocks(doc).forEach((entry, index) => {
    out[paletteEntryName(entry, index)] = entry;
  });
  return out;
}

/**
 * Catalog key for a chrome slot.
 * `frame[].ref` may be the widget key, or a slot whose `chrome[slot].widget` names the catalog entry.
 */
export function catalogNameForChromePart(doc, part) {
  if (!part) return null;
  const widgets = getWidgets(doc);
  if (Object.prototype.hasOwnProperty.call(widgets, part)) return part;
  const instance = getChrome(doc)?.[part];
  const name = instance && typeof instance === "object" && typeof instance.widget === "string"
    ? instance.widget.trim()
    : "";
  if (name && Object.prototype.hasOwnProperty.call(widgets, name)) return name;
  return null;
}

export function getSurfaceCard(doc, surface) {
  if (!surface) return null;
  const pages = getPages(doc);
  if (Object.prototype.hasOwnProperty.call(pages, surface)) {
    return pages[surface];
  }
  const catalogName = catalogNameForChromePart(doc, surface);
  if (catalogName) return getWidgets(doc)[catalogName];
  return null;
}

/** Widget or page root. Cards that are not on `view` yet still publish a single `states` entry. */
export function getViewFromCard(card) {
  if (!card || typeof card !== "object") return null;
  if (card.view && typeof card.view === "object") return card.view;
  const states = card.states;
  if (Array.isArray(states)) {
    const state = states.find((entry) => entry && typeof entry === "object");
    if (state) return state;
  }
  return null;
}

export function isChromeWidgetSurface(doc, surface) {
  return listChromeFrameRefs(doc).includes(surface);
}

export function listComponentCardOptions(doc) {
  const options = [];
  for (const ref of listChromeFrameRefs(doc)) {
    options.push({ value: ref, label: ref });
  }
  for (const key of listPageKeys(doc)) {
    options.push({ value: key, label: key });
  }
  return options;
}

export function resolvePageView(doc, pageKey) {
  const page = getPages(doc)[pageKey];
  const view = getViewFromCard(page);
  return view ? cloneBlockTree(view) : null;
}
